import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { api } from '../api/client';

import { T } from '../styles/tokens';
import { Button, ListSkeleton, SkeletonBar, Input, Select, Textarea } from '../components/ui';
// ══════════════════════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════════════════════

interface TreeEntry { path: string; type: 'file' | 'dir'; size: number; sha: string; }
interface TreeNode { name: string; path: string; type: 'file' | 'dir'; size: number; sha: string; children: TreeNode[]; }
interface OpenTab { path: string; content: string; sha: string; language: string; modified: boolean; originalContent: string; }
interface CommitInfo { sha: string; fullSha: string; message: string; author: string; date: string; }
interface BranchInfo { name: string; sha: string; protected: boolean; }

// ══════════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════════

const EXT_ICONS: Record<string, string> = {
 ts: '', tsx: '', js: '', jsx: '', json: '', md: '',
 sql: '', yaml: '', yml: '', css: '', html: '', sh: '',
 py: '', toml: '', txt: '', env: '', gitignore: '',
 dockerfile: '', lock: '', svg: '', png: '', jpg: '',
};

function getIcon(path: string, type: string, open?: boolean): string {
 if (type === 'dir') return open ? '' : '';
  const ext = path.split('.').pop()?.toLowerCase() || '';
 return EXT_ICONS[ext] || '';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function buildTree(entries: TreeEntry[]): TreeNode[] {
  const root: TreeNode[] = [];
  const dirMap = new Map<string, TreeNode>();

  // Create dir nodes first
  entries.filter(e => e.type === 'dir').sort((a, b) => a.path.localeCompare(b.path)).forEach(e => {
    const parts = e.path.split('/');
    const node: TreeNode = { name: parts[parts.length - 1], path: e.path, type: 'dir', size: 0, sha: e.sha, children: [] };
    dirMap.set(e.path, node);
    const parentPath = parts.slice(0, -1).join('/');
    const parent = dirMap.get(parentPath);
    if (parent) parent.children.push(node);
    else root.push(node);
  });

  // Add files
  entries.filter(e => e.type === 'file').sort((a, b) => a.path.localeCompare(b.path)).forEach(e => {
    const parts = e.path.split('/');
    const node: TreeNode = { name: parts[parts.length - 1], path: e.path, type: 'file', size: e.size, sha: e.sha, children: [] };
    const parentPath = parts.slice(0, -1).join('/');
    const parent = dirMap.get(parentPath);
    if (parent) parent.children.push(node);
    else root.push(node);
  });

  // Sort children: dirs first, then files
  const sortChildren = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'dir' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    nodes.forEach(n => sortChildren(n.children));
  };
  sortChildren(root);
  return root;
}

// ══════════════════════════════════════════════════════════════════════════
// SYNTAX HIGHLIGHTING
// ══════════════════════════════════════════════════════════════════════════

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function highlightCode(code: string, language: string): string {
  const escaped = escapeHtml(code);
  const lines = escaped.split('\n');

  return lines.map(line => {
    let result = line;

    if (['typescript', 'tsx', 'javascript', 'jsx'].includes(language)) {
      // Comments first (so they override everything)
      result = result.replace(/(\/\/.*$)/gm, '<span style="color:#6A9955">$1</span>');
      // Strings
      result = result.replace(/(&quot;[^&]*?&quot;|&#39;[^&]*?&#39;|`[^`]*?`)/g, '<span style="color:#CE9178">$1</span>');
      // Keywords
      result = result.replace(/\b(const|let|var|function|async|await|return|if|else|for|while|import|export|from|class|interface|type|enum|extends|implements|new|throw|try|catch|finally|default|switch|case|break|continue|typeof|instanceof|void|null|undefined|true|false|as|of|in)\b/g, '<span style="color:#569CD6">$1</span>');
      // Numbers
      result = result.replace(/\b(\d+\.?\d*)\b/g, '<span style="color:#B5CEA8">$1</span>');
    } else if (language === 'json') {
      result = result.replace(/(&quot;[^&]*?&quot;)\s*:/g, '<span style="color:#9CDCFE">$1</span>:');
      result = result.replace(/:\s*(&quot;[^&]*?&quot;)/g, ': <span style="color:#CE9178">$1</span>');
      result = result.replace(/\b(true|false|null)\b/g, '<span style="color:#569CD6">$1</span>');
      result = result.replace(/\b(\d+\.?\d*)\b/g, '<span style="color:#B5CEA8">$1</span>');
    } else if (language === 'sql') {
      result = result.replace(/\b(SELECT|FROM|WHERE|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP|TABLE|INDEX|INTO|VALUES|SET|AND|OR|NOT|NULL|PRIMARY|KEY|FOREIGN|REFERENCES|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AS|ORDER|BY|GROUP|HAVING|LIMIT|OFFSET|DISTINCT|UNION|ALL|EXISTS|IN|LIKE|BETWEEN|CASE|WHEN|THEN|ELSE|END|BEGIN|COMMIT|ROLLBACK|PRAGMA|INTEGER|TEXT|REAL|BLOB|VARCHAR|BOOLEAN|DATETIME|UNIQUE|DEFAULT|CHECK|CONSTRAINT|CASCADE|RESTRICT)\b/gi, '<span style="color:#C586C0">$1</span>');
      result = result.replace(/(&#39;[^&]*?&#39;)/g, '<span style="color:#CE9178">$1</span>');
    } else if (language === 'markdown') {
      result = result.replace(/^(#{1,6}\s.*)$/gm, '<span style="color:#569CD6;font-weight:bold">$1</span>');
      result = result.replace(/\*\*([^*]+)\*\*/g, '<span style="font-weight:bold;color:#D4D4D4">**$1**</span>');
      result = result.replace(/`([^`]+)`/g, `<span style="color:#CE9178;background:${T.border};padding:0 3px;border-radius:2px">` + "$1" + `</span>`);
    } else if (language === 'yaml') {
      result = result.replace(/^(\s*[\w.-]+):/gm, '<span style="color:#9CDCFE">$1</span>:');
      result = result.replace(/(#.*$)/gm, '<span style="color:#6A9955">$1</span>');
    } else if (language === 'css') {
      result = result.replace(/([a-zA-Z-]+)\s*:/g, '<span style="color:#9CDCFE">$1</span>:');
      result = result.replace(/(#[0-9a-fA-F]{3,8})\b/g, '<span style="color:#CE9178">$1</span>');
    }

    return result;
  }).join('\n');
}

// ══════════════════════════════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════════════════════════════

const S = {
  container: { display: 'flex', flexDirection: 'column' as const, height: '100vh', background: T.bg, color: T.text, fontFamily: "'Cascadia Code','Fira Code','SF Mono',Consolas,monospace", fontSize: '13px' },
  header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '0 12px', height: '36px', background: T.bgInput, borderBottom: `1px solid ${T.border}`, flexShrink: 0 as const, overflow: 'hidden' as const },
  headerBtn: { background: 'none', border: 'none', color: T.text, cursor: 'pointer', padding: '4px 8px', borderRadius: '3px', fontSize: '12px', whiteSpace: 'nowrap' as const },
  body: { display: 'flex', flex: 1, overflow: 'hidden' as const },
  sidebar: { width: '260px', minWidth: '200px', background: T.bgSecondary, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column' as const, overflow: 'hidden' as const },
  sidebarHeader: { padding: '8px 12px', fontSize: '11px', textTransform: 'uppercase' as const, color: T.textSecondary, letterSpacing: '1px', fontWeight: 600 },
  filterInput: { width: '100%', background: T.bgInput, border: `1px solid ${T.borderHover}`, color: T.text, padding: '4px 8px', fontSize: '12px', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' as const, margin: '0 0 4px' },
  treeContainer: { flex: 1, overflowY: 'auto' as const, overflowX: 'hidden' as const },
  main: { flex: 1, display: 'flex', flexDirection: 'column' as const, overflow: 'hidden' as const },
  tabs: { display: 'flex', background: T.bgInput, borderBottom: `1px solid ${T.border}`, overflow: 'auto' as const, flexShrink: 0 as const },
  tab: { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', borderRight: `1px solid ${T.border}`, whiteSpace: 'nowrap' as const, minWidth: 0 },
  tabActive: { background: T.bg, borderTop: '2px solid #007ACC' },
  tabInactive: { background: T.bgInput, borderTop: '2px solid transparent' },
  tabClose: { opacity: 0.5, cursor: 'pointer', marginLeft: '6px', fontSize: '14px', lineHeight: 1 },
  breadcrumb: { display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 16px', fontSize: '12px', color: T.textMuted, background: T.bg, borderBottom: `1px solid ${T.border}`, flexShrink: 0 as const },
  breadcrumbSep: { color: T.textMuted, margin: '0 2px' },
  breadcrumbItem: { cursor: 'pointer', color: T.textSecondary },
  editor: { flex: 1, display: 'flex', overflow: 'auto' as const, position: 'relative' as const },
  lineNumbers: { padding: '8px 12px 8px 8px', textAlign: 'right' as const, color: T.textMuted, userSelect: 'none' as const, minWidth: '40px', lineHeight: '20px', flexShrink: 0 as const, background: T.bg },
  codeArea: { flex: 1, padding: '8px 16px', lineHeight: '20px', overflow: 'auto' as const, whiteSpace: 'pre' as const, tabSize: 2 },
  footer: { display: 'flex', alignItems: 'center', gap: '16px', padding: '0 12px', height: '24px', background: '#007ACC', color: T.text, fontSize: '11px', flexShrink: 0 as const },
  modalOverlay: { position: 'fixed' as const, inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '15vh', zIndex: 1000 },
  modalBox: { background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: '6px', width: '520px', maxHeight: '400px', display: 'flex', flexDirection: 'column' as const, boxShadow: `0 8px 32px ${T.bgOverlay}` },
  modalInput: { width: '100%', background: T.bgInput, border: 'none', color: T.text, padding: '10px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' as const },
  settingsPanel: { padding: '16px', background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: '6px', maxWidth: '500px', margin: '40px auto' },
  settingsInput: { width: '100%', background: T.bgInput, border: `1px solid ${T.borderHover}`, color: T.text, padding: '6px 10px', fontSize: '13px', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' as const, marginTop: '4px' },
  settingsLabel: { fontSize: '12px', color: T.textSecondary, marginTop: '12px', display: 'block' as const },
  btn: { background: '#007ACC', color: T.text, border: 'none', padding: '6px 14px', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' },
  btnSecondary: { background: T.bgInput, color: T.text, border: `1px solid ${T.borderHover}`, padding: '6px 14px', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' },
  welcomeMsg: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', flex: 1, color: T.textMuted, gap: '12px' },
};

// ══════════════════════════════════════════════════════════════════════════
// FILE TREE COMPONENT
// ══════════════════════════════════════════════════════════════════════════

function FileTreeItem({ node, depth, expandedDirs, toggleDir, onFileClick, activeFile }: {
  node: TreeNode; depth: number; expandedDirs: Set<string>;
  toggleDir: (path: string) => void; onFileClick: (path: string) => void; activeFile: string | null;
}) {
  const isOpen = expandedDirs.has(node.path);
  const isActive = node.path === activeFile;
  const indent = depth * 16;

  if (node.type === 'dir') {
    return (
      <div>
        <div
          onClick={() => toggleDir(node.path)}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '2px 8px 2px ' + (indent + 8) + 'px',
            cursor: 'pointer', fontSize: '13px', lineHeight: '22px',
            background: 'transparent',
          }}
          onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
          onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
        >
          <span style={{ fontSize: '10px', width: '12px', textAlign: 'center' }}>{isOpen ? '▾' : '▸'}</span>
          <span>{getIcon(node.path, 'dir', isOpen)}</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>
        </div>
        {isOpen && node.children.map(child => (
          <FileTreeItem key={child.path} node={child} depth={depth + 1} expandedDirs={expandedDirs} toggleDir={toggleDir} onFileClick={onFileClick} activeFile={activeFile} />
        ))}
      </div>
    );
  }

  return (
    <div
      onClick={() => onFileClick(node.path)}
      style={{
        display: 'flex', alignItems: 'center', gap: '4px',
        padding: '2px 8px 2px ' + (indent + 20) + 'px',
        cursor: 'pointer', fontSize: '13px', lineHeight: '22px',
        background: isActive ? 'rgba(38,79,120,0.5)' : 'transparent',
        borderLeft: isActive ? '2px solid #007ACC' : '2px solid transparent',
      }}
      onMouseEnter={(e: any) => { if (!isActive) e.currentTarget.style.background = T.bgCard }}
      onMouseLeave={(e: any) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
    >
      <span>{getIcon(node.path, 'file')}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════

export function AdminGithub() {
  // State
  const [tree, setTree] = useState<TreeEntry[]>([]);
  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [tabs, setTabs] = useState<OpenTab[]>([]);
  const [activeTabIdx, setActiveTabIdx] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [fileLoading, setFileLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [commitMsg, setCommitMsg] = useState('');
  const [branch, setBranch] = useState('main');
  const [branches, setBranches] = useState<BranchInfo[]>([]);
  const [latestCommit, setLatestCommit] = useState<CommitInfo | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showQuickOpen, setShowQuickOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [quickOpenQuery, setQuickOpenQuery] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [fileCommits, setFileCommits] = useState<CommitInfo[]>([]);
  const [wordWrap, setWordWrap] = useState(false);
  const [saving, setSaving] = useState(false);

  // Settings state
  const [settingsToken, setSettingsToken] = useState('');
  const [settingsOwner, setSettingsOwner] = useState('');
  const [settingsRepo, setSettingsRepo] = useState('');
  const [settingsBranch, setSettingsBranch] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [recentFiles, setRecentFiles] = useState<any[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [showRecent, setShowRecent] = useState(true);

  // Diff viewer state — renders in main editor area
  const [diffData, setDiffData] = useState<any>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffMode, setDiffMode] = useState(false); // when true, editor shows diff instead of code
  const [activeDiffKey, setActiveDiffKey] = useState<string | null>(null); // "sha:path"

  const editorRef = useRef<HTMLTextAreaElement>(null);
  const activeTab = activeTabIdx >= 0 && activeTabIdx < tabs.length ? tabs[activeTabIdx] : null;

  // ── Load recent files ──
  const loadRecentFiles = useCallback(async () => {
    setRecentLoading(true);
    try {
      const data = await api.get<any>('/admin/github/recent-files');
      setRecentFiles(data.files || []);
    } catch { /* silent */ }
    setRecentLoading(false);
  }, []);

  // ── Load/toggle file diff in main editor ──
  const toggleDiff = useCallback(async (sha: string, path: string) => {
    const key = `${sha}:${path}`;
    if (activeDiffKey === key && diffMode) {
      // Toggle off — back to normal code view
      setDiffMode(false);
      setActiveDiffKey(null);
      setDiffData(null);
      return;
    }
    // Open the file first (if not already open)
    openFile(path);
    setActiveDiffKey(key);
    setDiffMode(true);
    setDiffLoading(true);
    setDiffData(null);
    try {
      const data = await api.get<any>(`/admin/github/file-diff?sha=${sha}&path=${encodeURIComponent(path)}`);
      setDiffData(data);
    } catch { setDiffData(null); }
    setDiffLoading(false);
  }, [activeDiffKey, diffMode]);

  // ── Load tree ──
  const loadTree = useCallback(async (br?: string) => {
    setLoading(true);
    try {
      const data = await api.get<any>(`/admin/github/tree${br ? `?branch=${br}` : ''}`);
      const entries = (data.tree || []) as TreeEntry[];
      setTree(entries);
      setTreeNodes(buildTree(entries));
      // Auto-expand first level
      const firstDirs = entries.filter(e => e.type === 'dir' && !e.path.includes('/')).map(e => e.path);
      setExpandedDirs(new Set(firstDirs));
    } catch (e) {
      console.error('Failed to load tree', e);
    }
    setLoading(false);
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const data = await api.get<any>('/admin/github/branches');
      setBranches(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  }, []);

  const loadLatestCommit = useCallback(async (br?: string) => {
    try {
      const data = await api.get<any>(`/admin/github/commits${br ? `?branch=${br}` : ''}`);
      const commits = Array.isArray(data) ? data : [];
      if (commits.length > 0) setLatestCommit(commits[0]);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadTree();
    loadBranches();
    loadLatestCommit();
    loadRecentFiles();
  }, []);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        setShowQuickOpen(true);
        setQuickOpenQuery('');
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') {
        e.preventDefault();
        setShowSearch(true);
        setSearchQuery('');
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && editing) {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editing, editContent, activeTab]);

  // ── Open file ──
  const openFile = async (path: string) => {
    // Exit diff mode when opening a file normally
    if (diffMode && !activeDiffKey) { setDiffMode(false); setDiffData(null); }
    // Check if already open
    const existingIdx = tabs.findIndex(t => t.path === path);
    if (existingIdx >= 0) {
      setActiveTabIdx(existingIdx);
      setEditing(false);
      return;
    }

    setFileLoading(true);
    try {
      const data = await api.get<any>(`/admin/github/file?path=${encodeURIComponent(path)}`);
      const newTab: OpenTab = {
        path: data.path || path,
        content: data.content || '',
        sha: data.sha || '',
        language: data.language || 'text',
        modified: false,
        originalContent: data.content || '',
      };
      const newTabs = [...tabs, newTab];
      setTabs(newTabs);
      setActiveTabIdx(newTabs.length - 1);
      setEditing(false);
    } catch (e) {
      console.error('Failed to load file', e);
    }
    setFileLoading(false);
  };

  // ── Close tab ──
  const closeTab = (idx: number, e?: Event) => {
    e?.stopPropagation();
    const tab = tabs[idx];
    if (tab.modified && !confirm('Unsaved changes. Close anyway?')) return;
    const newTabs = tabs.filter((_, i) => i !== idx);
    setTabs(newTabs);
    if (activeTabIdx >= newTabs.length) setActiveTabIdx(newTabs.length - 1);
    else if (activeTabIdx === idx) setActiveTabIdx(Math.min(idx, newTabs.length - 1));
    if (editing) setEditing(false);
  };

  // ── Toggle dir ──
  const toggleDir = (path: string) => {
    setExpandedDirs(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  // ── Save file ──
  const handleSave = async () => {
    if (!activeTab || saving) return;
    setSaving(true);
    try {
      const data = await api.put<any>('/admin/github/file', {
        path: activeTab.path,
        content: editContent,
        message: commitMsg || `edit: ${activeTab.path.split('/').pop()} via WhiteLabelClaw Admin`,
        sha: activeTab.sha,
      });
      if (data.success) {
        const newTabs = [...tabs];
        newTabs[activeTabIdx] = { ...activeTab, content: editContent, originalContent: editContent, modified: false, sha: '' };
        setTabs(newTabs);
        setEditing(false);
        setCommitMsg('');
        // Reload to get new sha
        const fileData = await api.get<any>(`/admin/github/file?path=${encodeURIComponent(activeTab.path)}`);
        newTabs[activeTabIdx].sha = fileData.sha;
        newTabs[activeTabIdx].content = fileData.content;
        newTabs[activeTabIdx].originalContent = fileData.content;
        setTabs([...newTabs]);
      }
    } catch (e: any) {
      alert(`Save failed: ${e.message}`);
    }
    setSaving(false);
  };

  // ── Search ──
  const doSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const data = await api.get<any>(`/admin/github/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  // ── File history ──
  const loadFileHistory = async () => {
    if (!activeTab) return;
    try {
      const data = await api.get<any>(`/admin/github/commits?path=${encodeURIComponent(activeTab.path)}`);
      setFileCommits(Array.isArray(data) ? data : []);
      setShowHistory(true);
    } catch (e) { console.error(e); }
  };

  // ── Branch change ──
  const handleBranchChange = async (newBranch: string) => {
    setBranch(newBranch);
    setTabs([]);
    setActiveTabIdx(-1);
    await loadTree(newBranch);
    await loadLatestCommit(newBranch);
  };

  // ── Settings ──
  const loadSettings = async () => {
    try {
      const data = await api.get<any>('/admin/github/config');
      setSettingsOwner(data.owner || '');
      setSettingsRepo(data.repo || '');
      setSettingsBranch(data.branch || '');
      setSettingsToken('');
    } catch (e) { console.error(e); }
  };

  const saveSettings = async () => {
    setSettingsLoading(true);
    try {
      const body: any = {};
      if (settingsToken) body.token = settingsToken;
      if (settingsOwner) body.owner = settingsOwner;
      if (settingsRepo) body.repo = settingsRepo;
      if (settingsBranch) body.branch = settingsBranch;
      await api.put('/admin/github/config', body);
      await loadTree();
      await loadBranches();
      await loadLatestCommit();
      setShowSettings(false);
    } catch (e: any) {
      alert(`Save failed: ${e.message}`);
    }
    setSettingsLoading(false);
  };

  const testConnection = async () => {
    try {
      const data = await api.get<any>('/admin/github/test-connection');
      setConnectionStatus(data);
    } catch (e: any) {
      setConnectionStatus({ connected: false, error: e.message });
    }
  };

  // ── Filtered tree ──
  const filterTree = (nodes: TreeNode[], q: string): TreeNode[] => {
    if (!q) return nodes;
    const lower = q.toLowerCase();
    return nodes.reduce<TreeNode[]>((acc, node) => {
      if (node.type === 'file' && node.name.toLowerCase().includes(lower)) {
        acc.push(node);
      } else if (node.type === 'dir') {
        const filtered = filterTree(node.children, q);
        if (filtered.length > 0) acc.push({ ...node, children: filtered });
      }
      return acc;
    }, []);
  };

  // ── Quick open files ──
  const quickOpenFiles = tree.filter(e => e.type === 'file').filter(e => {
    if (!quickOpenQuery) return true;
    const lower = quickOpenQuery.toLowerCase();
    return e.path.toLowerCase().includes(lower);
  }).slice(0, 20);

  // ── Create new file ──
  const createNewFile = () => {
    const path = prompt('Enter file path (e.g., apps/api/src/new-file.ts):');
    if (!path) return;
    const ext = path.split('.').pop()?.toLowerCase() || '';
    const lang = { ts: 'typescript', tsx: 'tsx', js: 'javascript', json: 'json', md: 'markdown', sql: 'sql', yaml: 'yaml', css: 'css', html: 'html' }[ext] || 'text';
    const newTab: OpenTab = { path, content: '', sha: '', language: lang, modified: true, originalContent: '' };
    const newTabs = [...tabs, newTab];
    setTabs(newTabs);
    setActiveTabIdx(newTabs.length - 1);
    setEditing(true);
    setEditContent('');
  };

  const displayedTree = filterTree(treeNodes, filter);
  const lines = activeTab ? (editing ? editContent : activeTab.content).split('\n') : [];

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  return (
    <div style={S.container}>
      {/* ── HEADER ── */}
      <div style={S.header}>
 <Button variant="ghost" size="xs" onClick={() => { setShowSettings(true); loadSettings(); }} title="Settings"></Button>
        <Select
          value={branch}
          onChange={(v) => handleBranchChange(v)}
          options={branches.length > 0 ? branches.map(b => ({ value: b.name, label: `🌿 ${b.name}` })) : [{ value: branch, label: `🌿 ${branch}` }]}
          style={{ background: T.bgInput, border: `1px solid ${T.borderHover}`, color: T.text, padding: '2px 6px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer' }}
        />
        {latestCommit && (
          <span
            style={{ fontSize: '11px', color: T.textSecondary, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}
            title={`${latestCommit.fullSha} — ${latestCommit.message}`}
            onClick={() => window.open(`https://github.com/${settingsOwner || ''}/${settingsRepo || ''}/commit/${latestCommit.fullSha}`, '_blank')}
          >
            {latestCommit.sha} — {latestCommit.message.slice(0, 40)} — {timeAgo(latestCommit.date)}
          </span>
        )}
        <div style={{ flex: 1 }} />
 <Button variant="ghost" size="xs" onClick={() => { setShowQuickOpen(true); setQuickOpenQuery(''); }} title="Quick Open (Ctrl+P)">Quick Open</Button>
 <Button variant="ghost" size="xs" onClick={() => { setShowSearch(true); setSearchQuery(''); setSearchResults([]); }} title="Search (Ctrl+Shift+F)">Search</Button>
 {!showRecent && <Button variant="ghost" size="xs" onClick={() => setShowRecent(true)} title="Show Recent Changes">Recent</Button>}
      </div>

      {/* ── BODY ── */}
      <div style={S.body}>
        {/* ── SIDEBAR ── */}
        <div style={S.sidebar}>
          <div style={{ ...S.sidebarHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Explorer</span>
            <Button variant="ghost" size="xs" onClick={createNewFile} title="New File">+</Button>
          </div>
          <div style={{ padding: '0 8px' }}>
            <Input
              placeholder="Filter files..."
              value={filter}
              onChange={(v) => setFilter(v)}
              style={S.filterInput}
            />
          </div>
          <div style={S.treeContainer}>
            {loading ? (
              <div style={{ padding: '16px', textAlign: 'center', color: T.textMuted }}>Loading...</div>
            ) : (
              displayedTree.map(node => (
                <FileTreeItem
                  key={node.path}
                  node={node}
                  depth={0}
                  expandedDirs={expandedDirs}
                  toggleDir={toggleDir}
                  onFileClick={openFile}
                  activeFile={activeTab?.path || null}
                />
              ))
            )}
          </div>
        </div>

        {/* ── MAIN AREA ── */}
        <div style={S.main}>
          {/* Tabs */}
          {tabs.length > 0 && (
            <div style={S.tabs}>
              {tabs.map((tab, i) => (
                <div
                  key={tab.path}
                  onClick={() => { setActiveTabIdx(i); setEditing(false); }}
                  style={{ ...S.tab, ...(i === activeTabIdx ? S.tabActive : S.tabInactive) }}
                >
                  <span>{getIcon(tab.path, 'file')}</span>
                  <span>{tab.path.split('/').pop()}</span>
                  {tab.modified && <span style={{ color: '#E8AB53' }}>●</span>}
                  <span style={S.tabClose} onClick={(e: any) => closeTab(i, e)}>×</span>
                </div>
              ))}
            </div>
          )}

          {activeTab ? (
            <>
              {/* Breadcrumb */}
              <div style={S.breadcrumb}>
                {diffMode && (
                  <span
                    onClick={() => { setDiffMode(false); setActiveDiffKey(null); setDiffData(null); }}
                    style={{
                      background: '#da3633', color: T.text, padding: '1px 8px', borderRadius: 4,
                      fontSize: 10, fontWeight: 700, cursor: 'pointer', marginRight: 8,
                      letterSpacing: '0.5px',
                    }}
                  >DIFF </span>
                )}
                {activeTab.path.split('/').map((seg, i, arr) => (
                  <span key={i}>
                    {i > 0 && <span style={S.breadcrumbSep}> › </span>}
                    <span style={i === arr.length - 1 ? { color: T.text } : S.breadcrumbItem}>{seg}</span>
                  </span>
                ))}
                <div style={{ flex: 1 }} />
                {!editing && (
                  <>
                    <Button variant="ghost" size="xs" onClick={() => setWordWrap(!wordWrap)}>
                      {wordWrap ? 'Wrap' : '→ No Wrap'}
                    </Button>
 <Button variant="ghost" size="xs" onClick={() => { navigator.clipboard.writeText(activeTab.content); }}>Copy</Button>
 <Button variant="ghost" size="xs" onClick={loadFileHistory}>History</Button>
 <Button variant="ghost" size="xs" onClick={() => window.open(`https://github.com/${settingsOwner || ''}/${settingsRepo || ''}/blob/${branch}/${activeTab.path}`, '_blank')}>GitHub</Button>
 <Button variant="primary" size="xs" onClick={() => { setEditing(true); setEditContent(activeTab.content); setCommitMsg(''); }}>Edit</Button>
                  </>
                )}
                {editing && (
                  <>
                    <Input
                      placeholder="Commit message (optional)"
                      value={commitMsg}
                      onChange={(v) => setCommitMsg(v)}
                      style={{ ...S.settingsInput, width: '250px', marginTop: 0, fontSize: '11px', padding: '3px 8px' }}
                    />
                    <Button variant="primary" size="xs" onClick={handleSave} disabled={saving} loading={saving}>Save</Button>
                    <Button variant="ghost" size="xs" onClick={() => { setEditing(false); setEditContent(''); }}>Cancel</Button>
                  </>
                )}
              </div>

              {/* Code viewer / Editor / Diff */}
              <div style={S.editor}>
                {fileLoading ? (
                  <div style={{ ...S.welcomeMsg }}>Loading file...</div>
                ) : diffMode ? (
                  /* ── DIFF VIEW in main editor ── */
                  <div style={{ flex: 1, overflow: 'auto', background: T.bg }}>
                    {diffLoading && (
                      <div style={{ padding: 40 }}><ListSkeleton rows={4} /></div>
                    )}
                    {!diffLoading && !diffData?.patch && (
                      <div style={{ padding: 40, textAlign: 'center', color: T.textSecondary, fontSize: 13 }}>
                        {diffData ? 'Sem alterações visíveis neste commit' : 'Erro ao carregar diff'}
                      </div>
                    )}
                    {!diffLoading && diffData?.patch && (() => {
                      const patchLines = diffData.patch.split('\n');
                      let oldL = 0;
                      let newL = 0;
                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontFamily: "'Cascadia Code','Fira Code','SF Mono',Consolas,monospace", fontSize: 13, lineHeight: '20px' }}>
                          <tbody>
                            {patchLines.map((line: string, idx: number) => {
                              const isHunk = line.startsWith('@@');
                              const isAdd = line.startsWith('+') && !isHunk;
                              const isDel = line.startsWith('-') && !isHunk;
                              const isCtx = !isHunk && !isAdd && !isDel;

                              if (isHunk) {
                                const m = line.match(/@@ -(\d+),?\d* \+(\d+),?\d* @@/);
                                if (m) { oldL = parseInt(m[1] || '0'); newL = parseInt(m[2] || '0'); }
                                return (
                                  <tr key={idx}>
                                    <td colSpan={4} style={{
                                      padding: '6px 16px', background: 'rgba(56,139,253,0.12)',
                                      color: T.primary, fontSize: 12,
                                      borderTop: idx > 0 ? '1px solid #21262d' : 'none',
                                      borderBottom: '1px solid #21262d',
                                    }}>
                                      {line}
                                    </td>
                                  </tr>
                                );
                              }

                              let oNum = '';
                              let nNum = '';
                              if (isDel) { oNum = String(oldL++); }
                              else if (isAdd) { nNum = String(newL++); }
                              else if (isCtx) { oNum = String(oldL++); nNum = String(newL++); }

                              const bg = isAdd ? `${T.successBg}` : isDel ? 'rgba(248,81,73,0.10)' : 'transparent';
                              const clr = isAdd ? '#aff5b4' : isDel ? '#ffd7d5' : T.text;
                              const numClr = isAdd ? T.success : isDel ? T.danger : '#484f58';
                              const sign = isAdd ? '+' : isDel ? '-' : ' ';

                              return (
                                <tr key={idx} style={{ background: bg }}>
                                  <td style={{ width: 48, textAlign: 'right' as const, padding: '0 6px', color: numClr, fontSize: 12, userSelect: 'none' as const }}>{oNum}</td>
                                  <td style={{ width: 48, textAlign: 'right' as const, padding: '0 6px', color: numClr, fontSize: 12, userSelect: 'none' as const, borderRight: '1px solid #21262d' }}>{nNum}</td>
                                  <td style={{ width: 20, textAlign: 'center' as const, color: numClr, fontSize: 12, userSelect: 'none' as const, fontWeight: 700 }}>{sign}</td>
                                  <td style={{ padding: '0 16px', color: clr, whiteSpace: 'pre' as const }}>{line.slice(1) || ' '}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                ) : (
                  <>
                    <div style={S.lineNumbers}>
                      {lines.map((_, i) => (
                        <div key={i} style={{ height: '20px', fontSize: '12px' }}>{i + 1}</div>
                      ))}
                    </div>
                    {editing ? (
                      <Textarea
                        ref={editorRef}
                        value={editContent}
                        onInput={(e: any) => {
                          setEditContent(e.target.value);
                          if (activeTab) {
                            const newTabs = [...tabs];
                            newTabs[activeTabIdx] = { ...activeTab, modified: e.target.value !== activeTab.originalContent };
                            setTabs(newTabs);
                          }
                        }}
                        style={{
                          ...S.codeArea,
                          background: 'transparent', border: 'none', color: T.text, resize: 'none', outline: 'none',
                          fontFamily: "'Cascadia Code','Fira Code','SF Mono',Consolas,monospace", fontSize: '13px', lineHeight: '20px',
                          whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
                          wordBreak: wordWrap ? 'break-all' as const : 'normal' as const,
                        }}
                        spellcheck={false}
                      />
                    ) : (
                      <div
                        style={{ ...S.codeArea, whiteSpace: wordWrap ? 'pre-wrap' : 'pre', wordBreak: wordWrap ? 'break-all' as const : 'normal' as const }}
                        dangerouslySetInnerHTML={{ __html: highlightCode(activeTab.content, activeTab.language) }}
                      />
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div style={S.welcomeMsg}>
 <span style={{ fontSize: '48px' }}></span>
              <span style={{ fontSize: '16px' }}>Select a file to view</span>
              <span style={{ fontSize: '12px' }}>Ctrl+P to quick open</span>
            </div>
          )}
        </div>

        {/* ── RECENT FILES PANEL (right sidebar) ── */}
        {showRecent && (
          <div style={{
            width: 280, minWidth: 280, background: T.bgSecondary, borderLeft: '1px solid #3c3c3c',
            display: 'flex', flexDirection: 'column' as const, overflow: 'hidden',
          }}>
            <div style={{
              padding: '10px 12px', fontSize: '11px', fontWeight: 700, color: T.textSecondary,
              textTransform: 'uppercase' as const, letterSpacing: '0.5px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderBottom: `1px solid ${T.border}`,
            }}>
 <span>Recent Changes</span>
              <Button variant="ghost" size="xs" onClick={() => setShowRecent(false)} title="Hide panel">×</Button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' as const, padding: '4px 0' }}>
              {recentLoading ? (
                <div style={{ padding: '16px', textAlign: 'center', color: T.textMuted, fontSize: '12px' }}>Loading...</div>
              ) : recentFiles.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', color: T.textMuted, fontSize: '12px' }}>No recent changes</div>
              ) : (
                recentFiles.map((f: any, i: number) => (
                  <div
                    key={`${f.path}-${i}`}
                    onClick={() => openFile(f.path)}
                    style={{
                      padding: '6px 12px', cursor: 'pointer', fontSize: '12px',
                      borderBottom: '1px solid rgba(60,60,60,0.3)',
                      transition: 'background 100ms',
                    }}
                    onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
                    onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        fontSize: '10px', padding: '1px 4px', borderRadius: '3px', fontWeight: 700,
                        background: f.status === 'added' ? `${T.successBgStrong}` : f.status === 'removed' ? 'rgba(248,81,73,0.2)' : T.primaryBg,
                        color: f.status === 'added' ? T.success : f.status === 'removed' ? T.danger : T.primary,
                      }}>
                        {f.status === 'added' ? 'A' : f.status === 'removed' ? 'D' : 'M'}
                      </span>
                      <span style={{ color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {f.path.split('/').pop()}
                      </span>
                    </div>
                    <div style={{ fontSize: '10px', color: T.textMuted, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.path}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '2px', fontSize: '10px', color: '#6e7681', alignItems: 'center' }}>
                      <span style={{ color: T.success }}>+{f.additions}</span>
                      <span style={{ color: T.danger }}>-{f.deletions}</span>
                      <span>·</span>
                      <span>{f.commit_sha}</span>
                      <span>·</span>
                      <span>{timeAgo(f.date)}</span>
                      {(f.additions > 0 || f.deletions > 0) && (() => {
                        const diffKey = `${f.commit_sha}:${f.path}`;
                        const isActive = activeDiffKey === diffKey;
                        return (
                          <>
                            <span>·</span>
                            <span
                              onClick={(e: any) => { e.stopPropagation(); toggleDiff(f.commit_sha, f.path); }}
                              style={{
                                color: isActive ? T.bg : T.primary,
                                background: isActive ? T.primary : 'transparent',
                                padding: '0 4px', borderRadius: 3,
                                cursor: 'pointer', fontWeight: 600,
                                transition: 'all 0.15s',
                              }}
                            >Diff</span>
                          </>
                        );
                      })()}
                    </div>
                    <div style={{ fontSize: '10px', color: '#6e7681', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.commit_message}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={{ padding: '6px 12px', borderTop: '1px solid #3c3c3c', fontSize: '10px', color: T.textMuted, textAlign: 'center' }}>
              {recentFiles.length} files · <span style={{ cursor: 'pointer', color: T.primary }} onClick={loadRecentFiles}>↻ Refresh</span>
            </div>
          </div>
        )}
      </div>

      {/* ── FOOTER ── */}
      <div style={S.footer}>
        {activeTab && (
          <>
            <span>{activeTab.path}</span>
            <span>|</span>
            <span>{formatSize(activeTab.content.length)}</span>
            <span>|</span>
            <span>{activeTab.content.split('\n').length} lines</span>
            <span>|</span>
            <span>{activeTab.language.toUpperCase()}</span>
          </>
        )}
        {!activeTab && <span>WhiteLabelClaw Admin — GitHub Explorer</span>}
        <div style={{ flex: 1 }} />
 <span> {branch}</span>
      </div>

      {/* ── QUICK OPEN MODAL ── */}
      {showQuickOpen && (
        <div style={S.modalOverlay} onClick={() => setShowQuickOpen(false)}>
          <div style={S.modalBox} onClick={(e: any) => e.stopPropagation()}>
            <input
              type="text"
              placeholder="Search files by name..."
              value={quickOpenQuery}
              onInput={(e: any) => setQuickOpenQuery(e.target.value)}
              onKeyDown={(e: any) => {
                if (e.key === 'Escape') setShowQuickOpen(false);
                if (e.key === 'Enter' && quickOpenFiles.length > 0) {
                  openFile(quickOpenFiles[0].path);
                  setShowQuickOpen(false);
                }
              }}
              style={S.modalInput}
              autoFocus
            />
            <div style={{ overflowY: 'auto', maxHeight: '320px' }}>
              {quickOpenFiles.map(f => (
                <div
                  key={f.path}
                  onClick={() => { openFile(f.path); setShowQuickOpen(false); }}
                  style={{ padding: '6px 14px', cursor: 'pointer', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}
                  onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
                  onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
                >
                  <span>{getIcon(f.path, 'file')}</span>
                  <span style={{ color: T.text }}>{f.path.split('/').pop()}</span>
                  <span style={{ color: T.textMuted, fontSize: '11px', marginLeft: 'auto' }}>{f.path}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SEARCH MODAL ── */}
      {showSearch && (
        <div style={S.modalOverlay} onClick={() => setShowSearch(false)}>
          <div style={S.modalBox} onClick={(e: any) => e.stopPropagation()}>
            <div style={{ display: 'flex' }}>
              <input
                type="text"
                placeholder="Search in files..."
                value={searchQuery}
                onInput={(e: any) => setSearchQuery(e.target.value)}
                onKeyDown={(e: any) => {
                  if (e.key === 'Escape') setShowSearch(false);
                  if (e.key === 'Enter') doSearch();
                }}
                style={{ ...S.modalInput, flex: 1 }}
                autoFocus
              />
              <Button variant="primary" size="sm" onClick={doSearch} style={{ borderRadius: 0 }}>Search</Button>
            </div>
            <div style={{ overflowY: 'auto', maxHeight: '320px' }}>
              {searchResults.map((r, i) => (
                <div
                  key={i}
                  onClick={() => { openFile(r.path); setShowSearch(false); }}
                  style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: `1px solid ${T.border}` }}
                  onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
                  onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ fontSize: '12px', color: T.text }}>{getIcon(r.path, 'file')} {r.path}</div>
                  {r.fragment && <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '4px', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{r.fragment}</div>}
                </div>
              ))}
              {searchResults.length === 0 && searchQuery && <div style={{ padding: '16px', color: T.textMuted, textAlign: 'center' }}>No results</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── HISTORY MODAL ── */}
      {showHistory && (
        <div style={S.modalOverlay} onClick={() => setShowHistory(false)}>
          <div style={S.modalBox} onClick={(e: any) => e.stopPropagation()}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${T.border}`, fontSize: '13px', fontWeight: 600 }}>
 File History — {activeTab?.path.split('/').pop()}
            </div>
            <div style={{ overflowY: 'auto', maxHeight: '320px' }}>
              {fileCommits.map((c, i) => (
                <div
                  key={i}
                  style={{ padding: '8px 14px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}
                  onClick={() => window.open(`https://github.com/${settingsOwner || ''}/${settingsRepo || ''}/commit/${c.fullSha}`, '_blank')}
                  onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
                  onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ fontSize: '12px' }}>
                    <span style={{ color: '#007ACC' }}>{c.sha}</span> — {c.message}
                  </div>
                  <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{c.author} • {timeAgo(c.date)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SETTINGS PANEL ── */}
      {showSettings && (
        <div style={S.modalOverlay} onClick={() => setShowSettings(false)}>
          <div style={{ ...S.settingsPanel, minWidth: '420px' }} onClick={(e: any) => e.stopPropagation()}>
 <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>GitHub Configuration</h3>

            <label style={S.settingsLabel}>GitHub Token</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Input
                type={showToken ? 'text' : 'password'}
                placeholder="ghp_..."
                value={settingsToken}
                onChange={(v) => setSettingsToken(v)}
                style={{ ...S.settingsInput, flex: 1 }}
              />
 <Button variant="ghost" size="xs" onClick={() => setShowToken(!showToken)}>{showToken ? '' : ''}</Button>
            </div>

            <label style={S.settingsLabel}>Owner / Organization</label>
            <Input value={settingsOwner} onChange={(v) => setSettingsOwner(v)} style={S.settingsInput} placeholder="owner" />

            <label style={S.settingsLabel}>Repository</label>
            <Input value={settingsRepo} onChange={(v) => setSettingsRepo(v)} style={S.settingsInput} placeholder="elon-tools" />

            <label style={S.settingsLabel}>Default Branch</label>
            <Input value={settingsBranch} onChange={(v) => setSettingsBranch(v)} style={S.settingsInput} placeholder="main" />

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <Button variant="primary" size="sm" onClick={saveSettings} disabled={settingsLoading} loading={settingsLoading}>Save</Button>
              <Button variant="secondary" size="sm" onClick={testConnection}>Test Connection</Button>
              <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>Cancel</Button>
            </div>

            {connectionStatus && (
              <div style={{ marginTop: '12px', padding: '8px 12px', borderRadius: '4px', background: connectionStatus.connected ? 'rgba(0,200,0,0.1)' : 'rgba(200,0,0,0.1)', border: `1px solid ${connectionStatus.connected ? '#4ec9b0' : '#f44'}` }}>
                {connectionStatus.connected ? (
 <span>Connected as <strong>{connectionStatus.user}</strong> — Rate limit: {connectionStatus.rateLimit?.remaining}/{connectionStatus.rateLimit?.limit}</span>
                ) : (
 <span>Error: {connectionStatus.error}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
